import React, { useState, useEffect } from 'react';
import { GetRequest, PostRequest } from 'components/common/index.js';
import { getDataSourceIcon, DataSourceConnectButtons } from './DataSourceIcons';

const DataSourcesSettings = () => {
  const [dataSources, setDataSources] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchDataSources = () => {
    setLoading(true);
    GetRequest({
      url: '/quote-builder/data-sources',
      callback: (response) => {
        setDataSources(response.data_sources || []);
        setLoading(false);
      },
      error: () => {
        setLoading(false);
      }
    });
  };

  useEffect(() => {
    fetchDataSources();

    // Listen for OAuth popup completion
    const handleMessage = (event) => {
      if (event.origin !== window.location.origin) return;
      if (event.data && (event.data.type === 'google_sheets_oauth_complete' || event.data.type === 'microsoft_excel_oauth_complete' || event.data.type === 'airtable_oauth_complete')) {
        fetchDataSources();
      }
    };

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, []);

  const handleConnectGoogleSheets = () => {
    openOAuthPopup('/quote-builder/data-sources/google-sheets/authorize', 'google_sheets_auth');
  };

  const handleConnectMicrosoftExcel = () => {
    openOAuthPopup('/quote-builder/data-sources/microsoft-excel/authorize', 'microsoft_excel_auth');
  };

  const handleConnectAirtable = () => {
    openOAuthPopup('/quote-builder/data-sources/airtable/authorize', 'airtable_auth');
  };

  const openOAuthPopup = (url, name) => {
    const width = 600;
    const height = 700;
    const left = window.screenX + (window.outerWidth - width) / 2;
    const top = window.screenY + (window.outerHeight - height) / 2;

    const popup = window.open(
      url,
      name,
      `width=${width},height=${height},left=${left},top=${top}`
    );

    const checkPopup = setInterval(() => {
      if (popup.closed) {
        clearInterval(checkPopup);
        fetchDataSources();
      }
    }, 500);
  };

  const handleUnlink = (dataSource) => {
    if (!confirm(t('quote_builder.unlink_data_source_confirm', { name: dataSource.name }))) {
      return;
    }

    PostRequest({
      url: `/quote-builder/data-sources/${dataSource.id}/unlink`,
      callback: () => {
        fetchDataSources();
      }
    });
  };

  const getSourceTypeIcon = (sourceType) => {
    return getDataSourceIcon(sourceType, { height: '20px', marginRight: '8px' });
  };

  const getSourceTypeName = (sourceType) => {
    if (sourceType === 'google_sheets') {
      return t('quote_builder.google_sheets');
    } else if (sourceType === 'microsoft_excel') {
      return t('quote_builder.microsoft_excel');
    } else if (sourceType === 'airtable') {
      return t('quote_builder.airtable');
    }
    return sourceType;
  };

  const getSyncStatusBadge = (status) => {
    const statusClasses = {
      'active': 'label-success',
      'pending': 'label-warning',
      'error': 'label-danger',
      'disconnected': 'label-default'
    };
    const statusLabels = {
      'active': t('quote_builder.sync_status_active'),
      'pending': t('quote_builder.sync_status_pending'),
      'error': t('quote_builder.sync_status_error'),
      'disconnected': t('quote_builder.sync_status_disconnected')
    };
    return <span className={`label ${statusClasses[status] || 'label-default'}`}>{statusLabels[status] || status}</span>;
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString() + ' ' + date.toLocaleTimeString();
  };

  const formatLinkedFiles = (linkedFiles) => {
    if (!linkedFiles || linkedFiles.length === 0) return '-';
    return linkedFiles.map((file, index) => (
      <span key={index}>
        {file}
        {index < linkedFiles.length - 1 && <br />}
      </span>
    ));
  };

  const dataSourceRow = (dataSource) => {
    return (
      <tr key={dataSource.id}>
        <td>
          {getSourceTypeIcon(dataSource.source_type)}
          {dataSource.name}
        </td>
        <td>{getSourceTypeName(dataSource.source_type)}</td>
        <td>{formatLinkedFiles(dataSource.linked_files)}</td>
        <td>{getSyncStatusBadge(dataSource.sync_status)}</td>
        <td>{formatDate(dataSource.last_synced_at)}</td>
        <td>
          <a href="#" onClick={(e) => { e.preventDefault(); handleUnlink(dataSource); }}>
            <i className="fa fa-chain-broken" aria-hidden="true"></i> {t('quote_builder.unlink')}
          </a>
        </td>
      </tr>
    );
  };

  const listOfDataSources = () => {
    return (
      <table className="table table-striped table-bordered-outside quote_builder_table">
        <tbody>
          <tr>
            <th colSpan="6">{t('quote_builder.my_data_sources')} ({dataSources.length})</th>
          </tr>
          <tr>
            <th>{t('quote_builder.name')}</th>
            <th>{t('quote_builder.source_type')}</th>
            <th>{t('quote_builder.linked_files')}</th>
            <th>{t('quote_builder.status')}</th>
            <th>{t('quote_builder.last_synced')}</th>
            <th></th>
          </tr>
          {dataSources.map(dataSourceRow)}
        </tbody>
      </table>
    );
  };

  if (loading) {
    return (
      <div style={{ marginLeft: '-20px' }}>
        <p><i className="fa fa-spinner fa-spin"></i> {t('quote_builder.loading')}</p>
      </div>
    );
  }

  return (
    <div style={{ marginLeft: '-20px' }}>
      <p style={{ marginBottom: '20px', color: '#666' }}>
        {t('quote_builder.data_sources_description')}
      </p>

      {dataSources.length > 0 && listOfDataSources()}

      <div style={{ marginTop: '20px' }}>
        <h4>{t('quote_builder.add_new_data_source')}</h4>
        <div style={{ marginTop: '15px' }}>
          <DataSourceConnectButtons
            onConnectGoogleSheets={handleConnectGoogleSheets}
            onConnectMicrosoftExcel={handleConnectMicrosoftExcel}
            onConnectAirtable={handleConnectAirtable}
          />
        </div>
        <p style={{ marginTop: '15px' }}>
          <a href="https://www.diecuttemplates.com/contact-us?chat=true" target="_blank" rel="noopener noreferrer">
            {t('quote_builder.missing_integration')}
          </a>
        </p>
      </div>

      {dataSources.length === 0 && (
        <div style={{ marginTop: '30px', padding: '20px', backgroundColor: '#f9f9f9', borderRadius: '4px' }}>
          <h4>{t('quote_builder.getting_started')}</h4>
          <p>{t('quote_builder.data_sources_getting_started_text')}</p>
        </div>
      )}
    </div>
  );
};

export default DataSourcesSettings;
